import { useState, useMemo } from "react";
import { ArrowUpDown, Search, X, Inbox } from "lucide-react";
import { Input } from "../ui/input";
import { Button } from "../ui/button";
import { useI18n } from "../../lib/i18n";

export function DataTable({ columns, rows, testId = "data-table", exportName = "export.csv", searchKeys, toolbar, onRowClick, pageSize = 25, selectable, selected, onSelect, rowClassName, chips, loading }) {
  const { t } = useI18n();
  const [q, setQ] = useState("");
  const [sort, setSort] = useState({ key: null, dir: 1 });
  const [page, setPage] = useState(0);

  const filtered = useMemo(() => {
    let data = rows || [];
    if (q) {
      const s = q.toLowerCase();
      const keys = searchKeys || columns.map((c) => c.key);
      data = data.filter((r) => keys.some((k) => String(r[k] ?? "").toLowerCase().includes(s)));
    }
    if (sort.key) {
      data = [...data].sort((a, b) => {
        const va = a[sort.key], vb = b[sort.key];
        if (va == null) return 1;
        if (vb == null) return -1;
        return (typeof va === "number" ? va - vb : String(va).localeCompare(String(vb))) * sort.dir;
      });
    }
    return data;
  }, [rows, q, sort, columns, searchKeys]);

  const pages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const view = filtered.slice(page * pageSize, (page + 1) * pageSize);
  const allChecked = selectable && view.length > 0 && view.every((r) => selected?.includes(r.id));
  const toggleAll = () => onSelect(allChecked ? selected.filter((id) => !view.some((r) => r.id === id)) : [...new Set([...(selected || []), ...view.map((r) => r.id)])]);
  const toggleOne = (id) => onSelect(selected?.includes(id) ? selected.filter((x) => x !== id) : [...(selected || []), id]);

  return (
    <div className="surface" data-testid={testId}>
      <div className="flex flex-wrap items-center gap-3 p-3 border-b border-slate-200">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
          <Input data-testid={`${testId}-search`} className="pl-8 h-9" placeholder={t("search")} value={q} onChange={(e) => { setQ(e.target.value); setPage(0); }} />
        </div>
        {toolbar}
      </div>
      {chips && chips.filter((c) => c && c.value).length > 0 && <div className="flex flex-wrap items-center gap-1.5 px-3 py-2 border-b border-slate-100 bg-slate-50/60" data-testid={`${testId}-chips`}>
        <span className="text-[11px] text-slate-500 mr-1">Filter aktif:</span>
        {chips.filter((c) => c && c.value).map((c) => <span key={c.key} className="chip chip-active" data-testid={`${testId}-chip-${c.key}`}>{c.label}: <b>{c.value}</b><button type="button" aria-label="Hapus filter" onClick={c.onClear} className="ml-0.5 rounded-full hover:bg-sky-200/60 p-0.5"><X className="h-3 w-3" /></button></span>)}
        {chips.filter((c) => c && c.value).length > 1 && <button type="button" data-testid={`${testId}-chips-clear`} onClick={() => chips.forEach((c) => c && c.value && c.onClear())} className="text-[11px] text-slate-500 underline ml-1">Hapus semua</button>}
      </div>}
      <div className="overflow-x-auto">
        <table className="w-full text-sm table-zebra">
          <thead>
            <tr className="table-head">
              {selectable && <th className="px-3 py-2.5 w-8"><input data-testid={`${testId}-select-all`} type="checkbox" checked={allChecked} onChange={toggleAll} /></th>}
              {columns.map((c) => (
                <th key={c.key} className="px-3 py-2.5 text-left whitespace-nowrap">
                  <button className={`inline-flex items-center gap-1 hover:text-slate-900 ${sort.key === c.key ? "text-[color:var(--brand-blue)]" : ""}`} onClick={() => setSort((s) => ({ key: c.key, dir: s.key === c.key ? -s.dir : 1 }))}>
                    {c.label} <ArrowUpDown className={`h-3 w-3 ${sort.key === c.key ? "opacity-100" : "opacity-40"}`} />
                  </button>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading && Array.from({ length: 5 }).map((_, i) => <tr key={`sk${i}`} className="border-t border-slate-100">{selectable && <td className="px-3 py-3"><span className="skeleton block h-4 w-4" /></td>}{columns.map((c) => <td key={c.key} className="px-3 py-3"><span className="skeleton block h-4 w-3/4" /></td>)}</tr>)}
            {!loading && view.length === 0 && (
              <tr><td colSpan={columns.length + (selectable ? 1 : 0)} className="px-3 py-12 text-center"><div className="mx-auto mb-2 grid h-10 w-10 place-items-center rounded-full bg-slate-100 text-slate-400"><Inbox className="h-5 w-5" /></div><p className="text-sm font-medium text-slate-600">{t("noData")}</p>{q && <p className="text-xs text-slate-400 mt-1">Tidak ada hasil untuk “{q}”</p>}</td></tr>
            )}
            {!loading && view.map((r, i) => (
              <tr key={`${r.id || r.student_id || ""}-${i}`} data-testid={`${testId}-row`} onClick={onRowClick ? () => onRowClick(r) : undefined}
                className={`border-t border-slate-100 transition-colors ${onRowClick ? "cursor-pointer" : ""} ${selected?.includes(r.id) ? "!bg-sky-50" : ""} ${rowClassName ? rowClassName(r) : ""}`}>
                {selectable && <td className="px-3 py-2.5" onClick={(e) => e.stopPropagation()}><input data-testid={`${testId}-select-row`} type="checkbox" checked={selected?.includes(r.id) || false} onChange={() => toggleOne(r.id)} /></td>}
                {columns.map((c) => (
                  <td key={c.key} className="px-3 py-2.5 align-middle">{c.render ? c.render(r) : r[c.key] ?? "-"}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {pages > 1 && (
        <div className="flex items-center justify-between px-3 py-2 border-t border-slate-200 text-xs text-slate-500">
          <span>{filtered.length} baris</span>
          <div className="flex gap-1">
            <Button size="sm" variant="ghost" disabled={page === 0} onClick={() => setPage(page - 1)} data-testid={`${testId}-prev`}>‹</Button>
            <span className="px-2 py-1">{page + 1}/{pages}</span>
            <Button size="sm" variant="ghost" disabled={page >= pages - 1} onClick={() => setPage(page + 1)} data-testid={`${testId}-next`}>›</Button>
          </div>
        </div>
      )}
    </div>
  );
}
